import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Phone, Download, CheckCircle2 } from "lucide-react";

const trustBadges = [
  "SAICA Registered",
  "SARS Compliance Support",
  "CIPC Annual Returns",
  "Payroll & UIF",
];

export function HeroSection() {
  return (
    <section className="relative overflow-hidden min-h-[90vh] bg-background">
      {/* SVG flowing curves */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 1440 900"
        preserveAspectRatio="xMidYMid slice"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path d="M-100,80 C100,20 250,160 400,100 C550,40 600,180 800,120" fill="none" stroke="hsl(173 80% 26% / 0.12)" strokeWidth="1.5" className="animate-fade-in" />
        <path d="M-120,110 C80,50 230,190 380,130 C530,70 580,210 780,150" fill="none" stroke="hsl(173 80% 26% / 0.10)" strokeWidth="1" />
        <path d="M-80,50 C120,0 270,130 420,70 C570,10 620,150 820,90" fill="none" stroke="hsl(173 80% 26% / 0.08)" strokeWidth="1" />
        <path d="M-140,140 C60,80 210,220 360,160 C510,100 560,240 760,180" fill="none" stroke="hsl(173 60% 35% / 0.10)" strokeWidth="1.5" />
        {[...Array(8)].map((_, i) => (
          <path
            key={`wave-top-${i}`}
            d={`M${-100 - i * 15},${60 + i * 12} C${100 + i * 10},${0 + i * 12} ${250 - i * 5},${150 + i * 8} ${400 + i * 10},${90 + i * 12} C${550 - i * 5},${30 + i * 12} ${600 + i * 10},${170 + i * 8} ${800 - i * 10},${110 + i * 12}`}
            fill="none"
            stroke={`hsl(173 70% 35% / ${0.04 + i * 0.01})`}
            strokeWidth="0.5"
          />
        ))}
        <path d="M1440,0 L1440,350 C1380,320 1320,400 1280,350 C1240,300 1200,200 1250,150 C1300,100 1360,50 1440,0Z" fill="hsl(173 80% 26% / 0.08)" />
        <path d="M1440,200 L1440,500 C1350,480 1300,520 1260,480 C1220,440 1280,360 1320,320 C1360,280 1400,240 1440,200Z" fill="hsl(173 80% 22% / 0.06)" />
        <path d="M1440,600 L1440,900 L1100,900 C1150,850 1180,780 1220,740 C1280,680 1350,720 1380,660 C1410,600 1420,640 1440,600Z" fill="hsl(173 80% 26% / 0.10)" />
        <path d="M1440,700 L1440,900 L1200,900 C1230,870 1260,830 1300,800 C1340,770 1380,740 1440,700Z" fill="hsl(173 85% 18% / 0.12)" />
        <path d="M0,900 L0,700 C80,720 140,780 180,820 C220,860 160,880 100,900Z" fill="hsl(173 60% 35% / 0.06)" />
        {[...Array(6)].map((_, i) => (
          <path
            key={`wave-bottom-${i}`}
            d={`M${800 + i * 20},${700 - i * 15} C${900 - i * 10},${750 + i * 10} ${1000 + i * 15},${680 - i * 10} ${1100 - i * 5},${730 + i * 15} C${1200 + i * 10},${780 - i * 10} ${1300 - i * 10},${720 + i * 12} ${1440 + i * 20},${760 - i * 15}`}
            fill="none"
            stroke={`hsl(173 70% 30% / ${0.06 + i * 0.01})`}
            strokeWidth="0.8"
          />
        ))}
      </svg>

      <div className="container mx-auto px-4 py-20 lg:py-32 relative z-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-muted rounded-full px-4 py-2 mb-8 animate-fade-up">
            <span className="w-2 h-2 rounded-full bg-primary"></span>
            <span className="text-sm font-medium text-foreground">
              Trusted by South African Businesses
            </span>
          </div>
          
          {/* Headline */}
          <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 animate-fade-up stagger-1 leading-tight text-center">
            <span className="text-gradient">Bookkeeping Services</span>
            <span className="block text-gradient">South Africa</span>
          </h1>
          
          {/* Subtext */}
          <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto animate-fade-up stagger-2 leading-relaxed">
            From monthly bookkeeping and VAT, payroll to annual financial statements, income tax and CIPC compliance — 
            we handle your financial admin so you can focus on growing your business.
          </p>
          
          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16 animate-fade-up stagger-3">
            <Link to="/book-a-call">
              <Button size="lg" className="btn-primary gap-2 text-base px-8 h-14">
                <Phone className="w-5 h-5" />
                Book a Call
              </Button>
            </Link>
            <a href="/BOOKKEEPING_SERVICES_SA_packages.pdf" download>
              <Button size="lg" variant="outline" className="btn-outline-primary gap-2 text-base px-8 h-14">
                View Packages
                <Download className="w-5 h-5" />
              </Button>
            </a>
          </div>
          
          {/* Trust Badges */}
          <div className="flex flex-wrap justify-center gap-3 animate-fade-up stagger-4">
            {trustBadges.map((badge) => (
              <div key={badge} className="trust-badge bg-primary/10 text-foreground border-primary/20">
                <CheckCircle2 className="w-4 h-4 text-primary" />
                {badge}
              </div>
            ))}
          </div>
          
        </div>
      </div>
    </section>
  );
}
